import { useCallback, useState } from 'react'

/**
 * Collapse state for the sidebar sections, persisted exactly as the spec names it
 * (docs/AGENTS-SIDEBAR-GROUP-NESTING.md): localStorage key `wt_sb_sections`, mapping
 * section id → expanded boolean. Default for v1 is all expanded.
 */
const KEY = 'wt_sb_sections'

type State = Record<string, boolean>

function read(): State {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? (parsed as State) : {}
  } catch {
    return {}
  }
}

export function useSections() {
  const [state, setState] = useState<State>(read)

  const isOpen = useCallback((id: string) => state[id] !== false, [state])

  const toggle = useCallback((id: string) => {
    setState((prev) => {
      const next = { ...prev, [id]: prev[id] === false }
      try {
        localStorage.setItem(KEY, JSON.stringify(next))
      } catch {
        // Private mode / quota — collapse state is a convenience, not correctness.
      }
      return next
    })
  }, [])

  return { isOpen, toggle }
}
